import { useState, useEffect } from 'react'
import { ticketsAPI } from '../../services/api'

import { logger } from '@/utils/logger'

const categories = [
  { value: 'general', label: 'Allgemeine Frage' },
  { value: 'technical', label: 'Technisches Problem' },
  { value: 'billing', label: 'Abrechnung & Zahlung' },
  { value: 'account', label: 'Account & Login' },
  { value: 'feature', label: 'Feature-Anfrage' },
  { value: 'cancellation', label: 'Kündigung' },
]

const statusConfig: Record<string, { className: string; label: string }> = {
  open: { className: 'wc-tag-outline', label: 'Offen' },
  in_progress: { className: 'wc-tag-outline', label: 'In Bearbeitung' },
  waiting_customer: { className: 'wc-tag-accent-2', label: 'Wartet auf Ihre Antwort' },
  resolved: { className: 'wc-tag-accent', label: 'Gelöst' },
  closed: { className: 'wc-tag-neutral', label: 'Geschlossen' },
}

export default function CustomerTicketsPage() {
  const [tickets, setTickets] = useState<any[]>([])
  const [selectedTicket, setSelectedTicket] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showNewTicketForm, setShowNewTicketForm] = useState(false)
  const [replyMessage, setReplyMessage] = useState('')
  const [formData, setFormData] = useState({ subject: '', category: 'general', message: '' })

  useEffect(() => {
    loadTickets()
  }, [])

  const loadTickets = async () => {
    setLoading(true)
    try {
      const response = await ticketsAPI.getMyTickets()
      setTickets(response.data)
    } catch (err) {
      logger.error('Error loading tickets:', err)
      setError('Fehler beim Laden der Tickets')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      await ticketsAPI.create({ subject: formData.subject, category: formData.category, message: formData.message })
      setSuccess('Ticket erfolgreich erstellt')
      setFormData({ subject: '', category: 'general', message: '' })
      setShowNewTicketForm(false)
      await loadTickets()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      logger.error('Error creating ticket:', err)
      setError('Fehler beim Erstellen des Tickets')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleAddReply = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!replyMessage.trim()) return

    setError('')
    setSuccess('')

    try {
      await ticketsAPI.addReply(selectedTicket.id, replyMessage)
      setSuccess('Antwort gesendet')
      setReplyMessage('')
      await loadTickets()

      const updatedTicket = tickets.find((t) => t.id === selectedTicket.id)
      if (updatedTicket) {
        setSelectedTicket(updatedTicket)
      }
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      logger.error('Error adding reply:', err)
      setError('Fehler beim Senden der Antwort')
      setTimeout(() => setError(''), 3000)
    }
  }

  const getStatusTag = (status: string) => {
    const c = statusConfig[status] || statusConfig.open
    return <span className={`wc-tag ${c.className}`}>{c.label}</span>
  }

  const getCategoryLabel = (category: string) => categories.find((c) => c.value === category)?.label || category

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('de-DE')
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 26, margin: '0 0 6px' }}>Support & Tickets</h1>
          <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>Verwalten Sie Ihre Support-Anfragen und Tickets</p>
        </div>
        <button onClick={() => setShowNewTicketForm(!showNewTicketForm)} className="wc-btn wc-btn-primary">
          {showNewTicketForm ? 'Abbrechen' : '+ Neues Ticket'}
        </button>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 24 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 24 }}>{success}</div>}

      {showNewTicketForm && (
        <div className="wc-card wc-elev-sm" style={{ marginBottom: 24 }}>
          <h3 className="wc-card-title">Neues Ticket erstellen</h3>
          <form onSubmit={handleCreateTicket} style={{ display: 'grid', gap: 16 }}>
            <div className="wc-field">
              <label>Betreff *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="wc-input"
                placeholder="Kurze Beschreibung Ihres Anliegens"
                required
              />
            </div>

            <div className="wc-field">
              <label>Kategorie *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="wc-input"
                required
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="wc-field">
              <label>Nachricht *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={5}
                className="wc-input"
                placeholder="Beschreiben Sie Ihr Anliegen im Detail…"
                required
              />
            </div>

            <button type="submit" className="wc-btn wc-btn-primary wc-btn-block">Ticket erstellen</button>
          </form>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <h3 className="wc-card-title" style={{ marginBottom: 8 }}>Keine Tickets</h3>
          <p className="wc-muted" style={{ marginBottom: 20 }}>Sie haben derzeit keine offenen Support-Tickets.</p>
          <button onClick={() => setShowNewTicketForm(true)} className="wc-btn wc-btn-primary">Neues Ticket erstellen</button>
        </div>
      ) : selectedTicket ? (
        <div className="wc-card wc-elev-sm">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <button onClick={() => setSelectedTicket(null)} className="wc-btn wc-btn-ghost" style={{ padding: 0 }}>
              ← Zurück zur Übersicht
            </button>
            {getStatusTag(selectedTicket.status)}
          </div>

          <h2 style={{ fontSize: 22, marginBottom: 16 }}>{selectedTicket.subject}</h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 16,
              marginBottom: 20,
              paddingBottom: 20,
              borderBottom: '1px solid var(--wc-divider)',
            }}
          >
            <div>
              <span className="wc-muted" style={{ fontSize: 12 }}>Ticket #</span>
              <p style={{ margin: 0, fontSize: 14 }}>{selectedTicket.id}</p>
            </div>
            <div>
              <span className="wc-muted" style={{ fontSize: 12 }}>Kategorie</span>
              <p style={{ margin: 0, fontSize: 14 }}>{getCategoryLabel(selectedTicket.category)}</p>
            </div>
            <div>
              <span className="wc-muted" style={{ fontSize: 12 }}>Erstellt</span>
              <p style={{ margin: 0, fontSize: 14 }}>{formatDateTime(selectedTicket.created_at)}</p>
            </div>
            <div>
              <span className="wc-muted" style={{ fontSize: 12 }}>Status</span>
              <p style={{ margin: '2px 0 0' }}>{getStatusTag(selectedTicket.status)}</p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {selectedTicket.replies?.filter((r: any) => !r.is_internal).map((reply: any) => (
              <div
                key={reply.id}
                style={{
                  padding: 14,
                  borderRadius: 'var(--wc-radius-md)',
                  background: reply.user_id
                    ? 'color-mix(in srgb, var(--wc-accent) 10%, transparent)'
                    : 'color-mix(in srgb, var(--wc-text) 6%, transparent)',
                  border: `1px solid ${reply.user_id ? 'color-mix(in srgb, var(--wc-accent) 30%, transparent)' : 'var(--wc-divider)'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13 }}>{reply.user_id ? 'Support-Team' : 'Sie'}</span>
                  <span className="wc-muted" style={{ fontSize: 11 }}>{formatDateTime(reply.created_at)}</span>
                </div>
                <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap' }}>{reply.message}</p>
              </div>
            ))}
          </div>

          {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
            <form onSubmit={handleAddReply} style={{ borderTop: '1px solid var(--wc-divider)', paddingTop: 20 }}>
              <div className="wc-field">
                <label>Ihre Antwort</label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                  className="wc-input"
                  placeholder="Ihre Nachricht…"
                  required
                  style={{ marginBottom: 12 }}
                />
              </div>
              <button type="submit" disabled={!replyMessage.trim()} className="wc-btn wc-btn-primary">
                Antwort senden
              </button>
            </form>
          )}

          {(selectedTicket.status === 'resolved' || selectedTicket.status === 'closed') && (
            <div className="wc-alert wc-alert-success" style={{ margin: 0 }}>
              <strong>Hinweis:</strong> Dieses Ticket wurde {selectedTicket.status === 'resolved' ? 'gelöst' : 'geschlossen'}.
              Bei weiteren Fragen erstellen Sie bitte ein neues Ticket.
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => setSelectedTicket(ticket)}
              className="wc-card wc-elev-sm wc-card-hover"
              style={{ cursor: 'pointer', gap: 12 }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <span className="wc-muted" style={{ fontSize: 13 }}>Ticket #{ticket.id}</span>
                {getStatusTag(ticket.status)}
              </div>

              <h3 className="wc-card-title">{ticket.subject}</h3>

              <div className="wc-muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{getCategoryLabel(ticket.category)}</span>
                <span>{ticket.replies?.length || 0} {ticket.replies?.length === 1 ? 'Nachricht' : 'Nachrichten'}</span>
              </div>

              <div
                className="wc-muted"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  paddingTop: 12,
                  borderTop: '1px solid var(--wc-divider)',
                }}
              >
                <span>Erstellt: {formatDateTime(ticket.created_at).split(',')[0]}</span>
                {ticket.status === 'waiting_customer' && (
                  <span style={{ color: 'var(--wc-accent-300)' }}>Antwort erforderlich</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
